import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";
import { Trash, Plus, EnvelopeSimple, Phone, User as UserIcon } from "@phosphor-icons/react";

const STAGES = ["New Lead", "Booked", "Qualified", "App Started", "Policy Issued", "Renewal"];

export default function Dashboard() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedId, setDraggedId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/leads");
      setLeads(r.data || []);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const move = async (id, stage) => {
    const prev = [...leads];
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, stage } : l)));
    try {
      await api.patch(`/leads/${id}`, { stage });
    } catch {
      setLeads(prev);
      toast.error("Move failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const seedDemo = async () => {
    const demos = [
      { name: "Marcus Bennett", email: "marcus@example.com", phone: "555-0143", goal: "Tax-free retirement at 60", source: "Instagram" },
      { name: "Tasha Williams", email: "tasha@example.com", phone: "555-0192", goal: "Roll over $80k 401k", source: "TikTok DM" },
      { name: "Diego Ramirez", email: "diego@example.com", phone: "555-0177", goal: "$5k/mo guaranteed income at 65", source: "Referral" },
    ];
    for (const d of demos) {
      await api.post("/leads/capture", d);
    }
    load();
    toast.success("Demo leads added");
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="eyebrow"><span className="gold-divider" />Pipeline</p>
            <h1 className="font-display text-4xl mt-3">EXODUS Pipeline</h1>
            <p className="text-sm text-exodus-muted mt-2">Drag a card to advance a lead through the funnel.</p>
          </div>
          <div className="flex gap-3">
            <button data-testid="seed-demo-leads" onClick={seedDemo} className="btn-ghost text-xs">
              <Plus size={14} weight="bold" /> Add demo leads
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-exodus-muted">Loading pipeline…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4">
            {STAGES.map((stage) => {
              const items = leads.filter((l) => l.stage === stage);
              return (
                <div
                  key={stage}
                  data-testid={`stage-column-${stage.toLowerCase().replace(/\s/g, "-")}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => draggedId && move(draggedId, stage)}
                  className="card-surface min-h-[400px] flex flex-col"
                >
                  <div className="px-4 py-3 border-b border-exodus-border flex items-center justify-between sticky top-0 bg-exodus-surface z-10">
                    <p className="eyebrow text-xs">{stage}</p>
                    <span className="font-mono text-xs gold-text">{items.length}</span>
                  </div>
                  <div className="p-3 space-y-3 flex-1">
                    {items.length === 0 && <p className="text-xs text-exodus-muted/50 italic text-center mt-8">empty</p>}
                    {items.map((lead) => (
                      <div
                        key={lead.id}
                        draggable
                        onDragStart={() => setDraggedId(lead.id)}
                        onDragEnd={() => setDraggedId(null)}
                        data-testid={`lead-card-${lead.id}`}
                        className="bg-exodus-elevated border border-exodus-border p-4 cursor-grab active:cursor-grabbing hover:border-exodus-gold transition-colors group"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-display text-lg truncate">{lead.name}</p>
                            <p className="text-xs text-exodus-muted truncate flex items-center gap-1 mt-1">
                              <EnvelopeSimple size={11} /> {lead.email}
                            </p>
                            {lead.phone && (
                              <p className="text-xs text-exodus-muted truncate flex items-center gap-1">
                                <Phone size={11} /> {lead.phone}
                              </p>
                            )}
                          </div>
                          <button
                            data-testid={`delete-lead-${lead.id}`}
                            onClick={() => remove(lead.id)}
                            className="opacity-0 group-hover:opacity-100 text-exodus-muted hover:text-exodus-oxblood transition-opacity"
                            aria-label="Delete lead"
                          >
                            <Trash size={14} />
                          </button>
                        </div>
                        {lead.goal && <p className="text-xs text-exodus-text/70 mt-3 line-clamp-2">{lead.goal}</p>}
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-exodus-border">
                          <span className="text-[10px] eyebrow text-exodus-muted">{lead.source}</span>
                          <span className="text-[10px] text-exodus-muted">
                            {new Date(lead.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && leads.length === 0 && (
          <div className="card-surface p-10 mt-8 text-center">
            <UserIcon size={32} weight="thin" className="mx-auto text-exodus-gold mb-4" />
            <h3 className="font-display text-2xl">No leads yet.</h3>
            <p className="text-sm text-exodus-muted mt-2">Capture your first lead via the public site contact form, or seed demo data above.</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
